import { useLocalization } from 'localization';
import { object, string, number, boolean, mixed, array, lazy, type AnySchema } from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { BookingTypes } from 'api';
import { REGEX } from 'constants/validation/regexes';

export type BookingFormData = {
  selectedDate: Dayjs;
  selectedTime: Dayjs | null;
  socialMediaType: string;
  slideShowMediaType: string;
  bookingType: BookingTypes;
  inputInstructions: string;
  proposalId: string;
  contactPerson: { label: string; value: string };
};

export const TEMPORARY_MAX_NUMBER_AREA = 10_000_000;

export const editAddressSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    fullAddress: string(),
    street: string().required(t('validation.mixed.required')),
    houseNumber: string()
      .required(t('validation.mixed.required'))
      .test('is-positive', t('validation.number.positive'), value => !value || value === '' || parseInt(value) >= 0),
    postNumber: string()
      .required(t('validation.mixed.required'))
      .test('is-positive', t('validation.number.positive'), value => !value || value === '' || parseInt(value) >= 0),
    city: string().required(t('validation.mixed.required')),
    floor: string().optional(),
    appartmentNumber: string().optional(),
    area: string(),
    longitude: number(),
    latitude: number(),
  });
};

export const contactPersonSchema = () => {
  const { t } = useLocalization();
  const trimToEmpty = (_value: unknown, originalValue: unknown) =>
    typeof originalValue === 'string' ? originalValue.trim() : originalValue ?? '';

  return object().shape({
    type: string().transform(trimToEmpty).notRequired(),
    name: string().transform(trimToEmpty).notRequired(),
    email: string().transform(trimToEmpty).notRequired().emailFormat(),
    phone: string()
      .transform(trimToEmpty)
      .notRequired()
      .test('min-if-present', t('common.placeholders.minChars', { min: 8 }), value => {
        if (!value) return true;
        return value.length >= 8;
      }),
    phoneAlt: string().transform(trimToEmpty).notRequired(),
  });
};

export const bookPhotographerSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    selectedDate: mixed().when('bookingType', {
      is: (value: string) => value === BookingTypes.BookingEsoft || value === BookingTypes.Booking3rdParty,
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema
          .required(t('validation.mixed.required'))
          .test('validateDate', t('validation.mixed.required'), value => dayjs.isDayjs(value) && value.isValid()),
    }),
    selectedTime: mixed()
      .nullable()
      .test('validateTime', t('validation.mixed.required'), value => dayjs.isDayjs(value) && value.isValid())
      .required(t('validation.mixed.required')),
    socialMediaType: string().default(''),
    slideShowMediaType: string().default(''),
    bookingType: string().default(''),
    inputInstructions: string().default(''),
    proposalId: string().default(''),
    contactPerson: object({
      label: string().default(''),
      value: string().default(''),
    })
      .nullable()
      .when('bookingType', {
        is: (value: string) => value === BookingTypes.Booking3rdParty,
        then: schema =>
          schema.test(
            'isContactPersonValid',
            t('validation.mixed.required'),
            select => !!select && select.label.trim() !== '' && select.value.trim() !== ''
          ),
        otherwise: schema => schema,
      }),
  });
};

//new version
export const createNewOrderSchemaV2 = () => {
  const { t } = useLocalization();
  return object().shape({
    //step1
    address: object().shape({
      full: string().required(t('validation.mixed.required')),
      street: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      houseNumber: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      floor: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      appartmentNumber: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      postNumber: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      city: string().when('full', value => {
        return value ? string().notRequired() : string().required(t('validation.mixed.required'));
      }),
      area: string().notRequired(),
    }),
    hasExternalStylist: boolean(),
    keyPickup: boolean(),
    pickUpKeyAddress: object().when('keyPickup', {
      is: true,
      then: () =>
        object({
          full: string().required(t('validation.mixed.required')),
          street: string().nullable(),
          houseNumber: string().nullable(),
          floor: string().nullable(),
          appartmentNumber: string().nullable(),
          postNumber: string().nullable(),
          city: string().nullable(),
          area: string().nullable(),
        }).required(),
      otherwise: () => object().notRequired(),
    }),
    shouldSendEndUserInvoice: boolean().required(),
    endUserInvoice: object().when('shouldSendEndUserInvoice', {
      is: true,
      then: () =>
        object({
          address: object({
            full: string().required(t('validation.mixed.required')),
            street: string().nullable(),
            houseNumber: string().nullable(),
            floor: string().nullable(),
            appartmentNumber: string().nullable(),
            postNumber: string().nullable(),
            city: string().nullable(),
            area: string().nullable(),
          }),
          name: string().required(t('validation.mixed.required')),
          email: string().emailFormat().required(t('validation.mixed.required')),
          phone: string()
            .required(t('validation.mixed.required'))
            .min(8, t('common.placeholders.minChars', { min: 8 })),
        }).required(),
      otherwise: () => object().notRequired(),
    }),
    shouldSendElectronicInvoice: boolean().required(),
    electronicInvoice: object().when('shouldSendElectronicInvoice', {
      is: true,
      then: () =>
        object({
          address: object({
            full: string().required(t('validation.mixed.required')),
            street: string().nullable(),
            houseNumber: string().nullable(),
            floor: string().nullable(),
            appartmentNumber: string().nullable(),
            postNumber: string().nullable(),
            city: string().nullable(),
            area: string().nullable(),
          }),
          name: string().required(t('validation.mixed.required')),
          email: string().emailFormat().required(t('validation.mixed.required')),
          phone: string()
            .required(t('validation.mixed.required'))
            .min(8, t('common.placeholders.minChars', { min: 8 })),
          cvr: string().required(t('validation.mixed.required')),
          ean: string().required(t('validation.mixed.required')),
          comment: string().required(t('validation.mixed.required')),
        }).required(),
      otherwise: () => object().notRequired(),
    }),
    property: object().shape({
      type: string().required(t('validation.mixed.required')),
      livingArea: number()
        .min(0, t('validation.number.min', { min: 0 }))
        .max(TEMPORARY_MAX_NUMBER_AREA, t('validation.number.max', { max: TEMPORARY_MAX_NUMBER_AREA }))
        .required(t('validation.mixed.required')),
      basementArea: number()
        .min(0, t('validation.number.min', { min: 0 }))
        .max(TEMPORARY_MAX_NUMBER_AREA, t('validation.number.max', { max: TEMPORARY_MAX_NUMBER_AREA }))
        .required(t('validation.mixed.required')),
      floors: number()
        .min(0, t('validation.number.min', { min: 0 }))
        .max(TEMPORARY_MAX_NUMBER_AREA, t('validation.number.max', { max: TEMPORARY_MAX_NUMBER_AREA }))
        .required(t('validation.mixed.required')),
    }),
    branchId: string().required(t('validation.mixed.required')),
    contactPersons: array()
      .of(contactPersonSchema())
      .required(t('validation.mixed.required'))
      .min(1, t('validation.mixed.required')),
    responsibleEmail: string().required(t('validation.mixed.required')),
    watcherEmails: array().of(string()),
    longitude: number(),
    latitude: number(),

    //step2:
    isCustomizePackage: boolean(),
    //step3:
    basicPackage: productSchema.nullable().optional(),
    webPackages: array().of(productSchema),
    somePackages: array().of(productSchema),
    textPackages: array().of(productSchema),
    dronePackages: array().of(productSchema),

    //step4:
    imageProducts: array().of(productSchema),
    filmProducts: array().of(productSchema),
    textProducts: array().of(productSchema),

    bundlePackagesProduct: lazy((value: unknown) => {
      if (!value || typeof value !== 'object') {
        return object().optional();
      }

      const record = value as Record<string, unknown>;
      const shape: Record<string, any> = {};

      for (const key of Object.keys(record)) {
        shape[key] = array().of(productSchema).optional();
      }

      return object(shape).optional();
    }).optional(),

    singleProducts: lazy((value: unknown) => {
      if (!value || typeof value !== 'object') {
        return object().optional();
      }

      const record = value as Record<string, unknown>;
      const shape: Record<string, any> = {};

      for (const key of Object.keys(record)) {
        shape[key] = array().of(productSchema).optional();
      }
      return object(shape).optional();
    }).optional(),
  });
};

export const productSchema = object({
  id: string().required(),
  productNumber: string().required(),
  name: string().required(),

  parentProductId: string().nullable(),
  displayPageSortedOrder: number().nullable(),
  displayPageId: string().nullable(),
  displayPage: string().nullable(),
  displayCategory: string().nullable(),
  displayCategoryOrder: number().nullable(),
  categoryId: string().nullable(),
  productStructure: string().required(),
  price: number().required(),
  discountedPrice: number().nullable(),
  quantity: number().min(0).nullable(),
  children: array()
    .of(lazy((): AnySchema => productSchema))
    .required(),
});
